import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { benachrichtigeModulAenderung } from '../hooks/useAktiveModule';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface ModulStatus {
  modul_key: string;
  aktiv: boolean;
}

interface ModulVerwaltungProps {
  tenantId: number;
}

export const MODUL_LABELS: Record<string, string> = {
  partner: 'Partner / Kunden',
  artikel: 'Artikel',
  lager: 'Lager',
  auftrag: 'Aufträge',
  bestellung: 'Einkauf / Bestellungen',
  fertigungsauftrag: 'Fertigungsaufträge',
  qualitaet: 'Qualitätssicherung',
  lieferschein: 'Lieferscheine',
  retoure: 'Retouren',
  zahlung: 'Zahlungen',
  sendung: 'Sendungen / Versand',
  projekt: 'Projekte',
  zeitbuchung: 'Zeitbuchung',
  mitarbeiter: 'Mitarbeiter',
  materialentnahme: 'Materialentnahme',
  vertrag: 'Verträge',
  einsatz: 'Einsatzplanung',
};

const ModulVerwaltung: React.FC<ModulVerwaltungProps> = ({ tenantId }) => {
  const [module, setModule] = useState<ModulStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [warnungen, setWarnungen] = useState<Record<string, string[]>>({});

  const load = useCallback(() => {
    setLoading(true);
    axios.get<ModulStatus[]>(`${API_URL}/api/mandant/${tenantId}/module`)
      .then(r => setModule(r.data))
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (modul_key: string, aktiv: boolean) => {
    setSaving(modul_key);
    try {
      const r = await axios.patch(`${API_URL}/api/mandant/${tenantId}/module/${modul_key}`, { aktiv });
      if (r.data.warnungen?.length) {
        setWarnungen(w => ({ ...w, [modul_key]: r.data.warnungen }));
      } else {
        setWarnungen(w => { const n = { ...w }; delete n[modul_key]; return n; });
      }
      load();
      benachrichtigeModulAenderung();
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div style={{ padding: 16 }}>Lade Modul-Konfiguration…</div>;

  return (
    <div style={{ padding: 16 }}>
      <h3 style={{ marginTop: 0 }}>Module verwalten</h3>
      <p style={{ color: 'var(--ink-2)', marginBottom: 16 }}>
        Deaktivierte Module verschwinden aus API und UI. Daten bleiben erhalten.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--bone-3)' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Modul</th>
            <th style={{ padding: '8px 12px', textAlign: 'center' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {module.map(m => (
            <React.Fragment key={m.modul_key}>
              <tr style={{ borderBottom: '1px solid var(--bone-3)' }}>
                <td style={{ padding: '8px 12px' }}>
                  <strong>{MODUL_LABELS[m.modul_key] || m.modul_key}</strong>
                  <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--ink-2)', fontFamily: 'monospace' }}>{m.modul_key}</span>
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                  <button
                    disabled={saving === m.modul_key}
                    onClick={() => handleToggle(m.modul_key, !m.aktiv)}
                    style={{
                      padding: '4px 14px', cursor: 'pointer', border: 'none', borderRadius: 0, fontSize: 13,
                      background: m.aktiv ? 'var(--green-500)' : 'var(--bone-3)', color: 'var(--bone)',
                    }}
                  >
                    {saving === m.modul_key ? '…' : m.aktiv ? 'Aktiv' : 'Inaktiv'}
                  </button>
                </td>
              </tr>
              {warnungen[m.modul_key]?.map((w, i) => (
                <tr key={i}>
                  <td colSpan={2} style={{ padding: '4px 12px 8px 24px', color: 'var(--gold)', fontSize: 13 }}>
                    {w}
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ModulVerwaltung;
